import { contactTypeTargetValues } from '@/entities/crm/contact/ContactType'
import { merge } from '@/utils/misc'
import { z } from '@/utils/zod'

export const organizationScopeActions = ['read', 'insert', 'edit', 'remove'] as const

export type OrganizationScopeAction = (typeof organizationScopeActions)[number]

export const organizationScopeCategories = [
    'general',
    'crm',
    'booking',
    'contracts',
    'sales',
    'work',
    'donation',
    'finance',
    'accounting',
    'communication',
    'misc',
    'portal',
    'connect',
    'legacy',
] as const

export type OrganizationScopeCategory = (typeof organizationScopeCategories)[number]

export type OrganizationScopeTarget = 'api' | 'user'

export type OrganizationScopeDefinition = {
    category: OrganizationScopeCategory
    target?: OrganizationScopeTarget | null
    actions?: readonly OrganizationScopeAction[]
    legacyName?: string
    requiresNewCRM?: boolean
}

const contactTypeScopeDefinitions = Object.fromEntries(
    contactTypeTargetValues.map((scope) => [scope, { category: 'crm', requiresNewCRM: true }] as const)
) as Record<(typeof contactTypeTargetValues)[number], { category: 'crm'; requiresNewCRM: true }>

// ATTENTION: always add the i18n when adding a new scope here
export const organizationScopeDefinitions = {
    // General
    organization: {
        category: 'general',
        actions: ['read', 'edit'],
        legacyName: 'organisation',
    },
    mandate: { category: 'general' },
    user: {
        category: 'general',
        actions: ['read'],
        legacyName: 'organisationUser',
    },
    tag: { category: 'general' },
    template: { category: 'general' },
    apiKey: { category: 'general', target: null },

    // CRM
    ...contactTypeScopeDefinitions,
    contactSegment: { category: 'crm' },
    contactHistory: { category: 'crm', requiresNewCRM: true },
    contactNote: { category: 'crm', requiresNewCRM: true },
    contactDocument: { category: 'crm', requiresNewCRM: true },
    applicationForm: { category: 'crm', requiresNewCRM: true },
    applicationSubmission: { category: 'crm', requiresNewCRM: true },
    contactPassTemplate: { category: 'crm', requiresNewCRM: true },
    contactIssuedPass: { category: 'crm', requiresNewCRM: true },
    contactGroupType: { category: 'crm', requiresNewCRM: true },
    contactGroup: { category: 'crm' },
    contactGroupMembership: { category: 'crm', requiresNewCRM: true },
    department: { category: 'crm', requiresNewCRM: true },
    compensationClaim: { category: 'crm', requiresNewCRM: true },
    // Approving creates receipts and payouts, so it is granted separately from reviewing claims
    compensationClaimApproval: { category: 'crm', requiresNewCRM: true, actions: ['edit'] },
    workHours: { category: 'crm', requiresNewCRM: true },
    // Settling creates receipts for fees and buyouts, so it is granted separately from reviewing entries
    workHoursSettlement: { category: 'crm', requiresNewCRM: true, actions: ['edit'] },
    contactRule: { category: 'crm', requiresNewCRM: true },
    contactReport: { category: 'crm' },
    crmSettings: { category: 'crm', requiresNewCRM: true },

    // Booking
    booking: { category: 'booking' },
    event: { category: 'booking' },
    eventOffer: { category: 'booking' },
    attendee: { category: 'booking' },
    site: { category: 'booking' },
    resource: { category: 'booking' },
    resourceOffer: { category: 'booking' },
    resourceReservation: { category: 'booking' },
    resourceCheckIn: { category: 'booking' },
    punchCard: { category: 'booking' },
    bookingDiscountCode: { category: 'booking' },
    supplementaryService: { category: 'booking' },
    bookingCategory: { category: 'booking' },
    bookingView: { category: 'booking' },

    // Contracts
    plan: { category: 'contracts', requiresNewCRM: true },
    contract: { category: 'contracts', requiresNewCRM: true },
    contractCollection: { category: 'contracts', requiresNewCRM: true },
    voucher: { category: 'contracts', requiresNewCRM: true },
    voucherTemplate: { category: 'contracts', requiresNewCRM: true },

    // Sales
    product: { category: 'sales' },
    inventory: { category: 'sales' },
    salesPeriod: { category: 'sales' },
    opportunity: { category: 'sales' },

    // Work
    todoItem: { category: 'work' },
    todoTopic: { category: 'work' },
    todoProject: { category: 'work' },

    // Donation
    donation: { category: 'donation' },
    donationForm: { category: 'donation' },
    donationFormSubmission: { category: 'donation' },
    austrianDonationSubmission: { category: 'donation' },
    donationsSettings: { category: 'donation' },

    // Finance
    financesSettings: { category: 'finance' },
    receipt: { category: 'finance' },
    receiptTextBlock: { category: 'finance' },
    onlineDebitCollection: { category: 'finance' },
    sepaDirectDebit: { category: 'finance' },
    sepaCreditTransfer: { category: 'finance' },
    cashAccount: { category: 'finance' },
    cashTransaction: { category: 'finance', legacyName: 'cashAccountTransaction' },
    cashTransactionRule: { category: 'finance' },
    cashRegister: { category: 'finance' },

    // Accounting
    posting: { category: 'accounting' },
    debtor: { category: 'accounting' },
    creditor: { category: 'accounting' },
    fixedAsset: { category: 'accounting' },
    accountingPlan: { category: 'accounting' },
    costCenter: { category: 'accounting' },

    // Communication
    conversation: { category: 'communication' },
    conversationFolder: { category: 'communication', requiresNewCRM: true },
    mailing: { category: 'communication', requiresNewCRM: true },
    social: { category: 'communication', requiresNewCRM: true },
    dispatchDocument: { category: 'communication' },
    notification: { category: 'communication', target: 'api' },
    questionnaireForm: { category: 'communication' },
    questionnaireSubmission: { category: 'communication' },

    // Misc
    drive: { category: 'misc' },
    certificateType: { category: 'misc', requiresNewCRM: true },
    certificate: { category: 'misc', requiresNewCRM: true },
    proofType: { category: 'misc', requiresNewCRM: true },
    proof: { category: 'misc', requiresNewCRM: true },
    publishedApp: { category: 'misc' },

    // Portal
    room: { category: 'portal', legacyName: 'space' },
    portalSettings: { category: 'portal' },

    // Connect
    team: { category: 'connect' },
    // Reviewing data changes needs a user, so API keys cannot get it
    selfServiceChange: { category: 'connect', target: 'user', actions: ['read', 'edit'] },

    // Legacy
    file: { category: 'legacy' },
    contact: { category: 'legacy' },
    contactTag: { category: 'legacy' },
    customFieldGroup: { category: 'legacy' },
    contactMembershipSection: { category: 'legacy' },
    outgoingMessage: { category: 'legacy' },
    messageTemplate: { category: 'legacy' },
    form: { category: 'legacy' },
    formSubmission: { category: 'legacy' },
    contributionRate: { category: 'legacy' },
    contribution: { category: 'legacy' },
    billing: { category: 'legacy' },
    billingLabel: { category: 'legacy' },
    invoice: { category: 'legacy' },
    cashAccountOrderStatus: { category: 'legacy' },
    accounting: { category: 'legacy' },
    bookEntry: { category: 'legacy' },
    walletPass: { category: 'legacy' },
    walletPassTemplate: { category: 'legacy' },
} as const satisfies Record<string, OrganizationScopeDefinition>

export type OrganizationScope = keyof typeof organizationScopeDefinitions

export const organizationScopes = Object.keys(organizationScopeDefinitions) as [
    OrganizationScope,
    ...OrganizationScope[],
]

export const OrganizationScopeSchema = z.enum(organizationScopes)

export const getOrganizationScopeDefinition = (scope: OrganizationScope): OrganizationScopeDefinition =>
    organizationScopeDefinitions[scope] as OrganizationScopeDefinition

export const getOrganizationScopeActions = (scope: OrganizationScope): readonly OrganizationScopeAction[] =>
    getOrganizationScopeDefinition(scope).actions ?? organizationScopeActions

export const isOrganizationScopeAvailableForTarget = (scope: OrganizationScope, target: OrganizationScopeTarget) => {
    const scopeTarget = getOrganizationScopeDefinition(scope).target
    return scopeTarget === undefined || scopeTarget === target
}

export const getOrganizationScopes = (target?: OrganizationScopeTarget): OrganizationScope[] =>
    organizationScopes.filter((scope) => !target || isOrganizationScopeAvailableForTarget(scope, target))

export const getOrganizationScopeLegacyName = (scope: OrganizationScope): string =>
    getOrganizationScopeDefinition(scope).legacyName ?? scope

export const OrganizationPermissionSchema = z.object({
    read: z.boolean(),
    insert: z.boolean(),
    edit: z.boolean(),
    remove: z.boolean(),
})

export type OrganizationPermission = z.infer<typeof OrganizationPermissionSchema>

export const OrganizationPermissionsSchema = z.preprocess(
    (data) => {
        // clear scopes that are not in the enum anymore,
        // then we don't have to update all the permissions when we remove a scope
        if (data && typeof data === 'object') {
            return Object.entries(data).reduce((obj, [key, value]) => {
                if (Object.values(OrganizationScopeSchema.options as any).includes(key)) {
                    obj[key] = value
                }
                return obj
            }, {} as any)
        }
        return data
    },
    z.partialRecord(OrganizationScopeSchema, OrganizationPermissionSchema)
)

export type OrganizationPermissions = z.infer<typeof OrganizationPermissionsSchema>

// Restrictions
export const organizationRestrictionScopes = ['mandate'] as const

export const OrganizationRestrictionScopeSchema = z.enum(organizationRestrictionScopes)

export type OrganizationRestrictionScope = z.infer<typeof OrganizationRestrictionScopeSchema>

// Areas whose records can be limited to the ones assigned to a user, e.g. as reviewer or assignee
export const organizationAssignedOnlyScopes = [
    'applicationSubmission',
    'donationFormSubmission',
    'selfServiceChange',
    'compensationClaim',
    'workHours',
    'todoItem',
    'drive',
] as const

export const OrganizationAssignedOnlyScopeSchema = z.enum(organizationAssignedOnlyScopes)

export type OrganizationAssignedOnlyScope = z.infer<typeof OrganizationAssignedOnlyScopeSchema>

// Missing keys mean no restriction, as for every restriction stored before the key existed
export const OrganizationRestrictionsSchema = z.object({
    mandate: z.array(z.objectId).optional(),
    assignedOnly: z.uniqueArray(OrganizationAssignedOnlyScopeSchema).optional(),
})

export type OrganizationRestrictions = z.infer<typeof OrganizationRestrictionsSchema>

// Custom Access
export const OrganizationCustomAccessSchema = z
    .object({
        contactTagsRestriction: z.object({
            enabled: z.boolean(),
            values: z.uniqueArray(z.string()),
            allowEmpty: z.boolean(),
        }),
        membershipSectionsRestriction: z.object({
            enabled: z.boolean(),
            values: z.uniqueArray(z.string()),
            allowEmpty: z.boolean(),
        }),
    })
    .partial()

export type OrganizationCustomAccess = z.infer<typeof OrganizationCustomAccessSchema>

// Access
export const OrganizationAccessSchema = z.object({
    organization: z.objectId,
    owner: z.boolean(),
    restrictions: OrganizationRestrictionsSchema,
    role: z.objectId.nullable(),
    permissions: OrganizationPermissionsSchema,
    // TODO: Remove this its from old campai when we cleaned up in new code completely
    customAccess: OrganizationCustomAccessSchema,
})

export type OrganizationAccess = z.infer<typeof OrganizationAccessSchema> & {
    portal: boolean
}

export const requiredOrganizationUserPermissions = {
    organization: {
        read: true,
    },
    mandate: {
        read: true,
    },
} satisfies Partial<Record<OrganizationScope, Partial<OrganizationPermission>>>

export const mergeOrganizationPermissions = (
    ...permissions: Partial<Record<OrganizationScope, Partial<OrganizationPermission>>>[]
): OrganizationPermissions => {
    const newPermissions: OrganizationPermissions = {}

    permissions.forEach((permission) => {
        ;(Object.keys(permission) as OrganizationScope[]).forEach((scope) => {
            newPermissions[scope] = {
                read: permission[scope]?.read || newPermissions[scope]?.read || false,
                insert: permission[scope]?.insert || newPermissions[scope]?.insert || false,
                edit: permission[scope]?.edit || newPermissions[scope]?.edit || false,
                remove: permission[scope]?.remove || newPermissions[scope]?.remove || false,
            }
        })
    })

    return newPermissions
}

export const removeOrganizationPermissions = (
    base: OrganizationPermissions,
    ...permissions: Partial<Record<OrganizationScope, Partial<OrganizationPermission>>>[]
): OrganizationPermissions => {
    const newPermissions = merge(base, {})

    permissions.forEach((permission) => {
        ;(Object.keys(permission) as OrganizationScope[]).forEach((scope) => {
            newPermissions[scope] = {
                read: permission[scope]?.read ? false : newPermissions[scope]?.read || false,
                insert: permission[scope]?.insert ? false : newPermissions[scope]?.insert || false,
                edit: permission[scope]?.edit ? false : newPermissions[scope]?.edit || false,
                remove: permission[scope]?.remove ? false : newPermissions[scope]?.remove || false,
            }
        })
    })

    return newPermissions
}

export const sanitizeOrganizationPermissions = (permissions: OrganizationPermissions): OrganizationPermissions => {
    const newPermissions = merge(permissions, {})

    ;(Object.keys(newPermissions) as OrganizationScope[]).forEach((scope) => {
        if (!newPermissions[scope].read) {
            newPermissions[scope].insert = false
            newPermissions[scope].edit = false
            newPermissions[scope].remove = false
        }
    })

    return newPermissions
}
